import { defineStore } from 'pinia';
import { useOrganizationStore } from '@/store/organizationStore';
import { apiRequestHandler } from '@/utils/api';
import {
  ItemsCacheKeys,
  PreparedFolder,
  PreparedItem,
  PreparedWorkItem,
  StoredQueryOptions,
  UpdateItem,
} from '@/models/item.models';
import {
  GanttItemType,
  OptimisticUpdateReturnType,
  PreparedGanttInitiative,
  PreparedGanttMilestone,
  PreparedGanttRoadmap,
  UpdateGanttItem,
} from '@/models/gantt.models';
import { prepareWorkItems } from './helpers';
import {
  getDependencyChainLookup,
  getGanttItemIdentifier,
  getInitiativeInferredDates,
} from '@/utils/roadmaps';
import { useItemStore } from './itemStore';
import { areFiltersApplied } from '@/utils/filters';
import { useFetchChildren } from '@/utils/fetch-children';
import { calculateMinMaxDatesFromChildren } from '@/components/gantt/utils';
import dayjs from '@/utils/dayjs';
import { clone, cloneDeep, get } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { LexoRank } from 'lexorank';
import { IPromiseQueue, usePromiseQueue } from '@/utils/promiseQueue';
import { ApiResponseBase } from '@/models/common.models';
import { ChannelEvents } from './events';
import {
  PusherInitiativeCreatedEvent,
  PusherInitiativeDeletedEvent,
  PusherInitiativeUpdatedEvent,
  PusherJobUpdatedEvent,
  PusherMilestoneCreatedEvent,
  PusherMilestoneDeletedEvent,
  PusherMilestoneUpdatedEvent,
  PusherScenarioCreatedEvent,
  PusherScenarioDeletedEvent,
  PusherScenarioUpdatedEvent,
} from '@/models/pusher.models';
import { toRaw, watch } from 'vue';
import {
  CreateRoadmap,
  UpdateRoadmap,
  FindItemsData,
  Folder,
  ItemType,
  JobStatus,
  Relationship,
  RelationshipEntity,
  RoadmapService,
  WorkItem,
  Roadmap,
  Initiative,
  Milestone,
  CreateRoadmapGroup,
  UpdateRoadmapGroup,
  RoadmapGroup,
  CreateInitiative,
  UpdateInitiative,
  CreateMilestone,
  UpdateMilestone,
  UpdateRoadmapItem,
  CreateScenario,
  UpdateScenario,
  RoadmapItem,
  Scenario,
  UpdateWorkItem,
} from '@/api';
import { useBoardStore } from './boardStore';
import { timeout } from '@/utils/time';

const isLocalDev = import.meta.env.MODE === 'development';
const isHostedDev = window.location.hostname.endsWith('.dev');
export const isDev = isLocalDev || isHostedDev;

// ============================================
// Async Update Timeout Constants
// ============================================

/**
 * Time in ms to wait for job creation confirmation via Pusher.
 * If no PENDING/RUNNING status is received within this time,
 * we assume no async job was needed and resolve immediately.
 */
const ASYNC_JOB_CREATION_TIMEOUT_MS = 5000;

/**
 * Maximum time in ms to wait for job completion via Pusher.
 * If the job is still running after this time, we assume it succeeded
 * and resolve to prevent UI from hanging indefinitely.
 */
const ASYNC_JOB_COMPLETION_TIMEOUT_MS = 60000;

/**
 * Base timeout in ms for dynamic timeout calculation when multiple items are affected.
 * Combined with DYNAMIC_TIMEOUT_PER_ITEM_MS to calculate total wait time.
 */
const DYNAMIC_TIMEOUT_BASE_MS = 5000;

/**
 * Additional timeout in ms per affected item for dynamic timeout calculation.
 * Formula: totalTimeout = min(DYNAMIC_TIMEOUT_BASE_MS + (itemCount * 2 * 1000), MAX_DYNAMIC_TIMEOUT_MS)
 */
const DYNAMIC_TIMEOUT_PER_ITEM_MS = 2000;

/**
 * Maximum dynamic timeout in ms to prevent excessive wait times for large datasets.
 * Caps the dynamic timeout calculation regardless of item count.
 */
const MAX_DYNAMIC_TIMEOUT_MS = 60000;

export type InteractionMode = 'strict' | 'flexible';

interface OptimisticUpdate {
  /** unique identifier for this update */
  updateKey: string;

  id: PreparedGanttMilestone['id'] | PreparedItem['number'];
  update: UpdateGanttItem;
  mode: InteractionMode;
}

/** Tracks pending async update handlers for cleanup */
interface PendingAsyncHandler {
  jobHandler: (event: PusherJobUpdatedEvent) => void;
  timeoutIds: ReturnType<typeof setTimeout>[];
  cleanup: () => void;
}

const calculateOptimisticTime = (
  roadmap: PreparedGanttRoadmap,
  optimisticUpdate: OptimisticUpdate,
  apply: boolean = false,
  /** Skip cloning when roadmap data is already cloned (for batch processing) */
  skipClone: boolean = false
): OptimisticUpdateReturnType => {
  // shallow copies (skip if already cloned by caller for batch processing)
  const initiatives = skipClone
    ? roadmap.initiatives
    : [...roadmap.initiatives].map((item) => clone(item));
  const milestones = skipClone
    ? roadmap.milestones
    : [...roadmap.milestones].map((item) => clone(item));
  const items = skipClone
    ? roadmap.items
    : [...roadmap.items].map((item) => clone(item));

  // helpers ----------------------------- //

  /** touched items as a result of this update and how they're touched :^) */
  const touchedItems: OptimisticUpdateReturnType = {};
  const touch = (
    item: PreparedGanttMilestone | PreparedItem | PreparedGanttInitiative,
    type: keyof OptimisticUpdateReturnType['0']
  ) => {
    const key = getGanttItemIdentifier(item);
    touchedItems[key] =
      touchedItems[key] ||
      ({
        orderUpdated: false,
        startDateUpdated: false,
        dueDateUpdated: false,
        relationshipsUpdated: false,
        boardIdUpdated: false,
      } satisfies OptimisticUpdateReturnType['0']);
    touchedItems[key][type] = true;
  };

  // easy lookup (also helps with hierarchy lookup)
  const itemsMap: Record<
    PreparedItem['number'] | PreparedGanttMilestone['id'] | Initiative['id'],
    PreparedGanttInitiative | PreparedGanttMilestone | PreparedItem
  > = {};
  [...initiatives, ...milestones, ...items].forEach((item) => {
    const key = getGanttItemIdentifier(item);
    itemsMap[key] = item;
  });

  const itemInitiativeMap: Record<
    PreparedItem['number'],
    PreparedGanttInitiative
  > = {};
  initiatives.forEach((initiative) => {
    initiative.itemNumbers.forEach((number) => {
      itemInitiativeMap[number] = initiative;
    });
  });

  const getItemDependencyType = (
    identifier: PreparedItem['number'] | PreparedGanttMilestone['id']
  ): 'workitem' | 'folder' | 'milestone' | null => {
    if (identifier in itemsMap) {
      const item = itemsMap[identifier];
      if (item.itemType === GanttItemType.MILESTONE) {
        return GanttItemType.MILESTONE;
      } else if (item.itemType === ItemType.WORKITEM) {
        return ItemType.WORKITEM;
      } else if (item.itemType === ItemType.FOLDER) {
        return ItemType.FOLDER;
      }
    }
    return null;
  };

  // easy dep chain lookup
  const itemDepMap = getDependencyChainLookup(items, milestones);

  /**
   * shifts dates of the entire dependency chain (up or down)
   * up = ancestors in the dependency chain (dependencies), down = descendants in the dependency chain (dependents)
   */
  const shiftDates = (
    item: PreparedGanttMilestone | PreparedItem,
    delta: number,
    propagationDirection: 'up' | 'down',
    skipSelf = false,
    _visited: Record<
      PreparedGanttMilestone['id'] | PreparedItem['number'],
      true
    > = {}
  ) => {
    const key = getGanttItemIdentifier(item);

    if (_visited[key]) return; // there's a cycle (bad bad bad)
    _visited[key] = true;

    if (!skipSelf) {
      if (item.itemType === GanttItemType.MILESTONE) {
        item.dueDate = dayjs(item.dueDate).add(delta, 'day').toISOString();
        touch(item, 'dueDateUpdated');
      } else {
        if (item.startDate) {
          item.startDate = dayjs(item.startDate)
            .add(delta, 'day')
            .toISOString();
          touch(item, 'startDateUpdated');
        }
        if (item.dueDate) {
          item.dueDate = dayjs(item.dueDate).add(delta, 'day').toISOString();
          touch(item, 'dueDateUpdated');
        }
      }
    }

    const propagationKey =
      propagationDirection === 'up' ? 'dependencies' : 'dependents';

    itemDepMap[key]?.[propagationKey].forEach((dependency) => {
      shiftDates(dependency, delta, propagationDirection, false, _visited);
    });
  };

  /**
   * infers dates for items from self or ancestors in the hierarchy (NOT the dependency chain)
   */
  const getInferredDates = (item: PreparedItem) => {
    let startDate = item.startDate;
    let dueDate = item.dueDate;

    // both set, no need to infer
    if (startDate && dueDate) {
      return { startDate, dueDate };
    }

    // if the item has an estimate, infer duration from that to set a more accurate start/due date
    let inferredDurationInDays = 0;

    const estimate =
      item.itemType === ItemType.WORKITEM
        ? item.estimate
        : item.baseItemAggregations?.totalEstimate;

    if (estimate) {
      inferredDurationInDays = Math.ceil(estimate / 24);
      --inferredDurationInDays; // subtract one day because we use inclusive dates
      if (inferredDurationInDays < 0) inferredDurationInDays = 0;
    }

    // infer from one of the set dates
    if (startDate || dueDate) {
      if (startDate) {
        dueDate = dayjs(startDate)
          .add(inferredDurationInDays, 'day')
          .toISOString();
      } else {
        startDate = dayjs(dueDate)
          .subtract(inferredDurationInDays, 'day')
          .toISOString();
      }
      return { startDate, dueDate };
    }

    // neither set, infer from prepared initiative (only possible for direct children of initiatives)
    const initiative = itemInitiativeMap[item.number];
    if (initiative) {
      startDate = initiative.inferredStartDate;
      dueDate = dayjs(startDate)
        .add(inferredDurationInDays, 'day')
        .toISOString();
      return { startDate, dueDate };
    }

    // neither set, infer from ancestors (or use fallback)
    const fallbackDate = dayjs().startOf('day').toISOString();
    startDate = fallbackDate;
    dueDate = dayjs(startDate).add(inferredDurationInDays, 'day').toISOString();

    let parent = item.parentNumber ? itemsMap[item.parentNumber] : null;
    if (!parent) return { startDate, dueDate };

    parent = parent as PreparedItem;
    while (true) {
      if (parent.startDate) {
        startDate = parent.startDate;
        dueDate = dayjs(startDate)
          .add(inferredDurationInDays, 'day')
          .toISOString();
        break;
      }

      // TODO: infer from initiative if we reach a parent that's a direct child of an initiative

      parent = parent.parentNumber ? itemsMap[parent.parentNumber] : null;
      if (!parent) break;
      parent = parent as PreparedItem;
    }
    return { startDate, dueDate };
  };

  /**
   * Recalculates a parent's minDate/maxDate based on ALL its children's dates.
   * This handles both expansion (child moves beyond parent) and contraction
   * (child moves back within bounds, shrinking the extended range).
   */
  const recalculateAncestorMinMaxDates = (
    childItem: PreparedItem,
    visited: Set<PreparedItem['number']> = new Set()
  ) => {
    if (!childItem.parentNumber) {
      return;
    }

    const parent = itemsMap[childItem.parentNumber] as PreparedItem | undefined;
    if (!parent || !parent.canHaveChildren) {
      return;
    }

    // Prevent infinite loops
    if (visited.has(parent.number)) return;
    visited.add(parent.number);

    // Find all direct children of this parent
    const children = items.filter(
      (i) => i.parentNumber === parent.number
    ) as PreparedItem[];

    // Use shared utility to calculate min/max dates from children
    const { minDate, maxDate } = calculateMinMaxDatesFromChildren(children);
    parent.minDate = minDate;
    parent.maxDate = maxDate;

    // Recursively update grandparents
    recalculateAncestorMinMaxDates(parent, visited);
  };

  const writeUpdates = () => {
    if (!apply) return;
    roadmap.initiatives = initiatives;
    roadmap.milestones = milestones;
    roadmap.items = items;
  };

  // ---------------------------------------------------- //

  const item = itemsMap[optimisticUpdate.id];
  if (!item) {
    return touchedItems;
  }
  const update = optimisticUpdate.update;
  const mode = optimisticUpdate.mode;

  // handle boardId updates
  if (update.boardId) {
    if (item.itemType === ItemType.WORKITEM) {
      item.boardId = update.boardId;
      item.board = useBoardStore().allCyclesAndBoards.find(
        (board) => board.id === update.boardId
      );
      touch(item, 'boardIdUpdated');
    }
  }

  // handle rank updates
  if (update.useWhichRank) {
    let below = update.rankBelow ? LexoRank.parse(update.rankBelow) : null;
    let above = update.rankAbove ? LexoRank.parse(update.rankAbove) : null;
    if (below || above) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (!below) below = above!.genPrev();
      if (!above) above = below.genNext();
      let newRank: string;
      try {
        newRank = below.between(above).toString();
      } catch {
        newRank = below.toString();
      }
      if (
        item.itemType === GanttItemType.MILESTONE ||
        item.itemType === GanttItemType.INITIATIVE
      ) {
        item.rank = newRank;
      } else {
        item.parentRank = newRank;
      }
      touch(item, 'orderUpdated');
    }
  }

  // date/dependency stuff doesn't apply to initiatives
  if (item.itemType === GanttItemType.INITIATIVE) {
    writeUpdates();
    return touchedItems;
  }

  // handle date updates
  if (update.startDate || update.dueDate) {
    if (mode === 'strict') {
      // recursive updates to ancestors and descendants

      if (item.itemType === GanttItemType.MILESTONE) {
        if (update.dueDate) {
          const oldDueDate = dayjs(item.dueDate);
          const newDueDate = dayjs(update.dueDate);
          const delta = newDueDate.diff(oldDueDate, 'day');
          shiftDates(item, delta, 'down'); // move dependents by delta
        }
      } else {
        const oldStartDate = item.startDate ? dayjs(item.startDate) : null;
        const newStartDate = update.startDate;

        const oldDueDate = item.dueDate ? dayjs(item.dueDate) : null;
        const newDueDate = update.dueDate;

        const action = (() => {
          // both dates changed
          if (oldStartDate && newStartDate && oldDueDate && newDueDate)
            return 'move';
          // only start date changed
          if (oldStartDate && newStartDate && oldDueDate && !newDueDate)
            return 'resize-start';
          // only due date changed
          if (oldDueDate && newDueDate && oldStartDate && !newStartDate)
            return 'resize-end';

          return 'other'; // basically, either start or due date is going from null to a value or vice versa
        })();

        if (action === 'move') {
          const delta = dayjs(newStartDate).diff(oldStartDate, 'day');
          shiftDates(item, delta, 'down'); // shift dependents
        } else if (action === 'resize-start') {
          item.startDate = newStartDate as string;
          touch(item, 'startDateUpdated');
        } else if (action === 'resize-end') {
          const delta = dayjs(newDueDate).diff(oldDueDate, 'day');
          item.dueDate = newDueDate as string;
          touch(item, 'dueDateUpdated');
          shiftDates(item, delta, 'down', true);
        } else {
          // no special handling needed for other cases (null to value or vice versa)

          if (newStartDate || newStartDate === null) {
            item.startDate = newStartDate || undefined;
            touch(item, 'startDateUpdated');
          }
          if (newDueDate || newDueDate === null) {
            item.dueDate = newDueDate || undefined;
            touch(item, 'dueDateUpdated');
          }
        }
      }
    } else {
      // handle startDate and dueDate updates (immediate targets only)
      if (item.itemType === GanttItemType.MILESTONE) {
        if (update.dueDate) {
          item.dueDate = update.dueDate;
          touch(item, 'dueDateUpdated');
        }
      } else {
        if (update.startDate) {
          item.startDate = update.startDate;
          touch(item, 'startDateUpdated');
        }
        if (update.dueDate) {
          item.dueDate = update.dueDate;
          touch(item, 'dueDateUpdated');
        }
      }
    }

    // NOTE: We intentionally preserve the item's minDate/maxDate here (no clamping).
    // These values represent the extent of the item's CHILDREN's dates, not the item's own dates.
    // When dragging a folder, the children haven't moved, so minDate/maxDate remain accurate.
    // The backend provides these values even for unloaded children, so we trust them.
    // Ancestor minDate/maxDate recalculation is handled later at line ~634 for all touched items.
  }

  // handle dependency updates
  if (update.relationshipsAdded) {
    // consumer should make sure there are no duplicates
    item.relationships = [
      ...(item.relationships || []),
      ...update.relationshipsAdded.map(
        (r): Relationship => ({
          type: r.relationType,
          // just add 'workitem' as a placeholder because this isn't permanent and the value isn't used either
          entity:
            getItemDependencyType(r.entityId) === 'workitem'
              ? RelationshipEntity.WORKITEM
              : getItemDependencyType(r.entityId) === 'folder'
              ? RelationshipEntity.FOLDER
              : getItemDependencyType(r.entityId) === 'milestone'
              ? RelationshipEntity.MILESTONE
              : RelationshipEntity.WORKITEM,
          referenceId: r.entityId,
        })
      ),
    ];
    touch(item, 'relationshipsUpdated');
  }
  if (update.relationshipsRemoved) {
    item.relationships = (item.relationships || []).filter(
      (d) => !update.relationshipsRemoved?.includes(d.referenceId)
    );
    touch(item, 'relationshipsUpdated');
  }

  // strict mode date shifting based on relationships
  if (mode === 'strict' && update.relationshipsAdded) {
    const dependent = item;

    // set dependent dates if needed
    if (
      dependent.itemType === ItemType.FOLDER ||
      dependent.itemType === ItemType.WORKITEM
    ) {
      if (!dependent.startDate || !dependent.dueDate) {
        const { startDate, dueDate } = getInferredDates(dependent);
        dependent.startDate = startDate;
        dependent.dueDate = dueDate;
        touch(dependent, 'startDateUpdated');
        touch(dependent, 'dueDateUpdated');
      }
    }

    update.relationshipsAdded.forEach((addedRel) => {
      if (addedRel.relationType !== 'blocked_by') return; // only blocked_by relationships can shift dates
      const dependency = itemsMap[addedRel.entityId];
      if (!dependency) return;
      if (dependency.itemType === GanttItemType.INITIATIVE) return; // narrowing

      // set dates for relationships that don't have them
      if (
        dependency.itemType === ItemType.FOLDER ||
        dependency.itemType === ItemType.WORKITEM
      ) {
        if (!dependency.startDate || !dependency.dueDate) {
          const { startDate, dueDate } = getInferredDates(dependency);
          dependency.startDate = startDate;
          dependency.dueDate = dueDate;
          touch(dependency, 'startDateUpdated');
          touch(dependency, 'dueDateUpdated');
        }
      }

      // shift dependent dates if needed
      const dependencyEndDate = dayjs(dependency.dueDate);
      if (dependent.itemType === GanttItemType.MILESTONE) {
        const milestoneDate = dayjs(dependent.dueDate);
        if (milestoneDate.isBefore(dependencyEndDate, 'day')) {
          const delta = dependencyEndDate
            .add(1, 'day')
            .diff(milestoneDate, 'day');
          shiftDates(dependent, delta, 'down');
        }
      } else {
        const dependentStartDate = dayjs(dependent.startDate);
        if (dependentStartDate.isBefore(dependencyEndDate, 'day')) {
          const delta = dependencyEndDate
            .add(1, 'day')
            .diff(dependentStartDate, 'day');
          shiftDates(dependent, delta, 'down');
        }
      }
    });
  }

  // update ancestor min/max dates

  // Recalculate ancestor min/max dates to show/hide the candy-striped visual indicator
  // This handles both expansion (child moves beyond parent) and contraction (child moves back within bounds)
  const useRecursiveMinMaxUpdates = true;
  if (useRecursiveMinMaxUpdates) {
    Object.entries(touchedItems).forEach(([key]) => {
      const touchedItem = itemsMap[key];
      if (!touchedItem) return;
      if (
        touchedItem.itemType === GanttItemType.INITIATIVE ||
        touchedItem.itemType === GanttItemType.MILESTONE
      )
        return; // narrowing

      // Recalculate parent's min/max dates from all children
      recalculateAncestorMinMaxDates(touchedItem);
    });
  } else {
    // just do a simple update of the min/max dates of the item being updated
    if (item.itemType !== GanttItemType.MILESTONE && item.canHaveChildren) {
      if (
        update.startDate &&
        item.minDate &&
        dayjs(update.startDate).isBefore(item.minDate, 'day')
      ) {
        item.minDate = update.startDate;
      }
      if (
        update.dueDate &&
        item.maxDate &&
        dayjs(update.dueDate).isAfter(item.maxDate, 'day')
      ) {
        item.maxDate = update.dueDate;
      }
    }
  }

  // update initiative min/max dates
  const preparedItemsMap = new Map<PreparedItem['number'], PreparedItem>();
  Object.values(itemsMap).forEach((item) => {
    if (item.itemType === GanttItemType.INITIATIVE) return; // narrowing
    if (item.itemType === GanttItemType.MILESTONE) return; // narrowing
    preparedItemsMap.set(item.number, item);
  });

  Object.entries(touchedItems).forEach(([itemId]) => {
    const initiative = itemInitiativeMap[itemId];
    if (!initiative) return;
    const inferredDates = getInitiativeInferredDates(
      initiative,
      preparedItemsMap
    );
    if (initiative.inferredStartDate !== inferredDates.startDate) {
      initiative.inferredStartDate = inferredDates.startDate;
    }
    if (initiative.inferredDueDate !== inferredDates.dueDate) {
      initiative.inferredDueDate = inferredDates.dueDate;
    }
  });

  writeUpdates();

  return touchedItems;
};

/** Cache entry for a prepared roadmap with version tracking */
interface PreparedRoadmapCacheEntry {
  roadmap: PreparedGanttRoadmap;
  /** Version of localAllItems when this was cached */
  itemsVersion: number;
  /** Version of optimistic updates when this was cached */
  optimisticVersion: number;
  /** Hash of raw roadmap data for invalidation */
  roadmapHash: string;
}

export interface GanttState {
  /** current mode */
  interactionMode: InteractionMode;
  /** list of all roadmaps. these DON'T have all the data needed to be selected and used in the chart */
  ganttRoadmapsList: Roadmap[];
  /** roadmaps in this cache are FULLY LOADED and can be used in the chart */
  ganttRoadmapsCache: Record<Roadmap['id'], Roadmap>;

  /** Cache for prepared roadmaps to avoid recomputation */
  _preparedRoadmapCache: Record<Roadmap['id'], PreparedRoadmapCacheEntry>;
  /** Version counter for localAllItems - incremented on changes */
  _itemsVersion: number;
  /** Version counter for optimistic updates per roadmap */
  _optimisticVersions: Record<Roadmap['id'], number>;
  /** contains info about which children are loaded so we don't make duplicate calls */
  ganttItemsChildrenLoadedMap: Record<
    PreparedItem['number'] | Initiative['id'],
    boolean
  >;

  ganttRoadmapsGroups: RoadmapGroup[];

  /** currently selected roadmap */
  currentGanttRoadmapId: Roadmap['id'] | null;

  /** collected optimistic updates */
  optimisticUpdatesMap: Record<Roadmap['id'], OptimisticUpdate[]>;

  /** queues for remote updates */
  updateQueues: Record<Roadmap['id'], IPromiseQueue>;

  /** helper flag for setting up remote update pipes */
  isSubscribedToAsyncUpdates: boolean;
  /** cleanup function for item update subscriptions - called on component unmount */
  _cleanupItemUpdates: (() => void) | null;
  /**
   * local copy of work items/folders in ItemCacheKeys.GANTT_ITEMS.
   * We use this to control when the remote updates are applied to the items to
   * provide a mostly seamless UX as we switch between optimistic and remote updates.
   *
   * Never use items from itemStore directly, always use this instead.
   */
  localAllItems: PreparedItem[];
  /** temporary storage for milestone updates to be applied to the roadmaps */
  pendingMilestoneUpdates: Milestone[];
  /** helper flag for flushing item and milestone updates */
  isItemUpdateQueued: boolean;

  /** flag for recording scenarios. When true, date updates are not sent to the server and are collected instead */
  scenarioModel:
    | {
        type: 'record';
        data: CreateScenario;
      }
    | {
        type: 'record-edit';
        data: Scenario;
      }
    | {
        type: 'baseline';
        data: Scenario;
      }
    | null;

  workOffline: boolean; // TODO: for testing, remove after QA

  /** Tracks pending async handlers for cleanup to prevent memory leaks */
  _pendingAsyncHandlers: PendingAsyncHandler[];
}

export const useGanttStore = defineStore('gantt', {
  /****
   STATE
   ****/
  state: (): GanttState => ({
    interactionMode: 'flexible',
    ganttRoadmapsList: [],
    ganttRoadmapsCache: {},
    _preparedRoadmapCache: {},
    _itemsVersion: 0,
    _optimisticVersions: {},
    ganttItemsChildrenLoadedMap: {},
    currentGanttRoadmapId: null,
    optimisticUpdatesMap: {},
    updateQueues: {},

    isSubscribedToAsyncUpdates: false,
    _cleanupItemUpdates: null,
    localAllItems: [],
    pendingMilestoneUpdates: [],
    isItemUpdateQueued: false,

    scenarioModel: null,
    workOffline: false,
    ganttRoadmapsGroups: [],
    _pendingAsyncHandlers: [],
  }),

  /****
   GETTERS
   ****/
  getters: {
    /**
     * Returns a fully prepared roadmap with items, initiatives, milestones, and computed properties.
     * Uses multi-layer caching with three invalidation triggers:
     *
     * Cache Invalidation Strategy:
     * - `_itemsVersion`: Incremented when localAllItems changes (item updates from Pusher/API)
     * - `_optimisticVersions[roadmapId]`: Incremented per roadmap when optimistic updates are added/removed
     * - `roadmapHash`: Content-aware hash of initiative/milestone/item IDs to detect structural changes
     *   (e.g., items added/removed from initiatives, milestones created/deleted)
     *
     * The cache is invalidated when ANY of these values change from the cached entry.
     */
    getPreparedRoadmap() {
      return (roadmapId: Roadmap['id']): PreparedGanttRoadmap | null => {
        const roadmap = this.ganttRoadmapsCache[roadmapId];
        if (!roadmap) return null;

        // Generate a content-aware hash for cache invalidation
        // Include IDs to detect structural changes (items added/removed from initiatives)
        // Include name, description, roadmapGroupId to detect metadata changes
        const initiativeIds = roadmap.initiatives.map((i) => i.id).join(',');
        const milestoneIds = roadmap.milestones.map((m) => m.id).join(',');
        const roadmapItemNumbers = roadmap.roadmapItems
          .map((r) => r.itemNumber)
          .join(',');
        const roadmapHash = `${roadmap.name}|${roadmap.description || ''}|${
          roadmap.roadmapGroupId || ''
        }|${initiativeIds}|${milestoneIds}|${roadmapItemNumbers}`;
        const optimisticVersion = this._optimisticVersions[roadmapId] || 0;

        // Check if we have a valid cached entry
        const cached = this._preparedRoadmapCache[roadmapId];
        const cacheHit =
          cached &&
          cached.itemsVersion === this._itemsVersion &&
          cached.optimisticVersion === optimisticVersion &&
          cached.roadmapHash === roadmapHash;

        if (cacheHit) {
          return cached.roadmap;
        }

        // Compute the prepared roadmap
        const preparedRoadmap = this._computePreparedRoadmap(roadmapId);
        if (!preparedRoadmap) return null;

        // Cache the result (mutating state directly in getter is fine for caching)
        this._preparedRoadmapCache[roadmapId] = {
          roadmap: preparedRoadmap,
          itemsVersion: this._itemsVersion,
          optimisticVersion,
          roadmapHash,
        };

        return preparedRoadmap;
      };
    },

    /**
     * Internal helper to compute a prepared roadmap. Separated for caching.
     */
    _computePreparedRoadmap() {
      return (roadmapId: Roadmap['id']): PreparedGanttRoadmap | null => {
        const roadmap = this.ganttRoadmapsCache[roadmapId];
        if (!roadmap) return null;

        // Build Set for O(1) lookup of initiative item numbers
        const initiativeItemNumbersSet = new Set<PreparedItem['number']>();
        roadmap.initiatives.forEach((initiative) => {
          initiative.itemNumbers.forEach((num) =>
            initiativeItemNumbersSet.add(num)
          );
        });
        const cachedItems = this.localAllItems;

        // O(n) filter using Set lookup instead of O(n*m) with includes()
        let items = initiativeItemNumbersSet.size
          ? cachedItems.filter((i) => initiativeItemNumbersSet.has(i.number))
          : [];

        // Build parent -> children Map once for O(1) child lookup
        const childrenByParent = new Map<
          PreparedItem['number'],
          PreparedItem[]
        >();
        cachedItems.forEach((item) => {
          if (item.parentNumber) {
            const existing = childrenByParent.get(item.parentNumber);
            if (existing) {
              existing.push(item);
            } else {
              childrenByParent.set(item.parentNumber, [item]);
            }
          }
        });

        // Populate loaded children using O(n) BFS traversal
        const processedNumbers = new Set(items.map((i) => i.number));
        const queue = [...items];
        while (queue.length) {
          const current = queue.shift()!;
          const children = childrenByParent.get(current.number);
          if (children) {
            for (const child of children) {
              if (!processedNumbers.has(child.number)) {
                processedNumbers.add(child.number);
                items.push(child);
                queue.push(child);
              }
            }
          }
        }

        const itemRankMap = new Map<
          PreparedItem['number'],
          RoadmapItem['rank']
        >();
        roadmap.roadmapItems.forEach((item) => {
          if (item.roadmapId !== roadmap.id) return;
          itemRankMap.set(item.itemNumber, item.rank);
        });

        items = items.map((item) => {
          const ret: PreparedItem = {
            ...item,
            // use initiative based rank for level 1 items
            parentRank: itemRankMap.get(item.number) || item.parentRank,
          };
          return ret;
        });
        // item preparation done
        const itemsMap = new Map<PreparedItem['number'], PreparedItem>();
        items.forEach((item) => itemsMap.set(item.number, item));

        // Recalculate minDate/maxDate for all expandable items based on their loaded children
        // This ensures the candy stripe reflects actual loaded data, not stale backend values
        const recalculateMinMaxForLoadedItems = () => {
          // Build children lookup for efficient access
          const childrenMap = new Map<PreparedItem['number'], PreparedItem[]>();
          items.forEach((item) => {
            if (item.parentNumber) {
              const siblings = childrenMap.get(item.parentNumber) || [];
              siblings.push(item);
              childrenMap.set(item.parentNumber, siblings);
            }
          });

          // Process items in reverse depth order (leaves first, then parents)
          // This ensures children's minDate/maxDate are calculated before parents
          const processedItems = new Set<PreparedItem['number']>();
          const processItem = (item: PreparedItem) => {
            if (processedItems.has(item.number)) return;

            // First process all children
            const children = childrenMap.get(item.number) || [];
            children.forEach(processItem);

            // Now calculate this item's minDate/maxDate from its children
            // Only recalculate if we have loaded children to calculate from
            // Otherwise, trust the backend-provided minDate/maxDate values
            if (item.canHaveChildren && children.length > 0) {
              // Use shared utility to calculate min/max dates from children
              const { minDate, maxDate } =
                calculateMinMaxDatesFromChildren(children);
              item.minDate = minDate;
              item.maxDate = maxDate;
            }

            processedItems.add(item.number);
          };

          // Process all items (the recursive function handles ordering)
          items.forEach(processItem);
        };
        recalculateMinMaxForLoadedItems();

        // prepare PreparedInitiative[] and PreparedMilestone[]
        const preparedInitiatives: PreparedGanttInitiative[] =
          roadmap.initiatives.map((initiative) => {
            const inferredDates = getInitiativeInferredDates(
              initiative,
              itemsMap
            );
            return {
              ...initiative,
              itemType: GanttItemType.INITIATIVE,
              inferredStartDate: inferredDates.startDate,
              inferredDueDate: inferredDates.dueDate,
            };
          });
        const preparedMilestones: PreparedGanttMilestone[] =
          roadmap.milestones.map((milestone) => ({
            ...milestone,
            itemType: GanttItemType.MILESTONE,
          }));

        // prepare original dates for items
        const originalDates: PreparedGanttRoadmap['originalDates'] = {};
        preparedInitiatives.forEach((initiative) => {
          originalDates[initiative.id] = {
            startDate: initiative.inferredStartDate,
            dueDate: initiative.inferredDueDate,
          };
        });
        roadmap.milestones.forEach((milestone) => {
          originalDates[milestone.id] = {
            startDate: undefined,
            dueDate: milestone.dueDate,
          };
        });
        items.forEach((item) => {
          originalDates[item.number] = {
            startDate: item.startDate || undefined,
            dueDate: item.dueDate || undefined,
          };
        });

        const preparedRoadmap: PreparedGanttRoadmap = {
          ...roadmap,
          initiatives: preparedInitiatives,
          milestones: preparedMilestones,
          items,
          originalDates,
          optimisticUpdatesMeta: {},
        };

        const updates = this.optimisticUpdatesMap[roadmapId];
        if (updates && updates.length > 0) {
          const updatesMeta: OptimisticUpdateReturnType = {};
          const mergeUpdatesMeta = (meta: OptimisticUpdateReturnType) => {
            Object.entries(meta).forEach(([key, value]) => {
              if (!updatesMeta[key]) {
                updatesMeta[key] = value;
                return;
              }
              const existing = updatesMeta[key];
              Object.entries(value).forEach(([key2, value2]) => {
                const currentKey =
                  key2 as keyof OptimisticUpdateReturnType['0'];
                if (existing[currentKey]) return; // don't change true values
                existing[currentKey] = value2;
              });
            });
          };

          // Build set of directly affected IDs for selective cloning
          const affectedIds = new Set(updates.map((u) => u.id));

          // Check if any update uses strict mode (cascading requires cloning all)
          const hasStrictModeUpdates = updates.some((u) => u.mode === 'strict');

          // Clone selectively: in flexible mode, only clone affected items
          // In strict mode, clone all to handle cascading date updates safely
          preparedRoadmap.initiatives = preparedRoadmap.initiatives.map(
            (item) =>
              hasStrictModeUpdates || affectedIds.has(item.id)
                ? clone(item)
                : item
          );
          preparedRoadmap.milestones = preparedRoadmap.milestones.map((item) =>
            hasStrictModeUpdates || affectedIds.has(item.id)
              ? clone(item)
              : item
          );
          preparedRoadmap.items = preparedRoadmap.items.map((item) =>
            hasStrictModeUpdates || affectedIds.has(item.number)
              ? clone(item)
              : item
          );

          updates.forEach((update) => {
            // Pass skipClone=true since we pre-cloned above
            const ret = calculateOptimisticTime(
              preparedRoadmap,
              update,
              true,
              true
            );
            mergeUpdatesMeta(ret);
          });
          preparedRoadmap.optimisticUpdatesMeta = updatesMeta;
        }

        return preparedRoadmap;
      };
    },
    currentGanttRoadmap(): PreparedGanttRoadmap | null {
      if (!this.currentGanttRoadmapId) return null;
      return this.getPreparedRoadmap(this.currentGanttRoadmapId);
    },
    isRecordingScenario(): boolean {
      return (
        this.scenarioModel?.type === 'record' ||
        this.scenarioModel?.type === 'record-edit'
      );
    },
    recordedScenarioUpdates(): OptimisticUpdate[] {
      if (!this.isRecordingScenario || !this.currentGanttRoadmapId) return [];
      return (
        this.optimisticUpdatesMap[this.currentGanttRoadmapId]?.filter(
          (u) => u.update.startDate || u.update.dueDate
        ) || []
      );
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    /** for storing work items and folders. No relation to `roadmap.roadmapItems`. */
    async _storeRoadmapItems(
      items: (WorkItem | Folder)[],
      { updateAsync = false }: { updateAsync?: boolean } = {}
    ) {
      const preparedItems = await prepareWorkItems(items);

      const itemStore = useItemStore();
      itemStore.addItems({
        items: preparedItems,
        keys: [ItemsCacheKeys.GANTT_ITEMS],
      });

      if (updateAsync) {
        this._queueItemUpdates();
      } else {
        // Efficient incremental update: only clone items that are new or changed
        const existingMap = new Map<PreparedItem['number'], PreparedItem>();
        this.localAllItems.forEach((i) => existingMap.set(i.number, i));

        preparedItems.forEach((item) => {
          const rawItem = toRaw(item);
          const existingItem = existingMap.get(rawItem.number);

          // New item - clone and add
          if (!existingItem) {
            existingMap.set(rawItem.number, cloneDeep(rawItem));
            return;
          }

          // Check if key fields changed
          const existingRaw = toRaw(existingItem);
          if (
            existingRaw.startDate !== rawItem.startDate ||
            existingRaw.dueDate !== rawItem.dueDate ||
            existingRaw.parentRank !== rawItem.parentRank ||
            existingRaw.relationships?.length !==
              rawItem.relationships?.length ||
            existingRaw.manualPercentCompleted !==
              rawItem.manualPercentCompleted
          ) {
            existingMap.set(rawItem.number, cloneDeep(rawItem));
          }
          // Otherwise keep existing item (no clone needed)
        });

        this.localAllItems = [...existingMap.values()];
        this._itemsVersion++;
      }
    },

    /**
     * internal utility to process and store fully loaded roadmap items
     * @param isPartialRoadmap set true if the roadmap is a partial roadmap (see docstring for Roadmap). This would be the case when updating a roadmap
     */
    _storeRoadmap(roadmap: Roadmap, isPartialRoadmap = false) {
      if (isPartialRoadmap && !this.ganttRoadmapsCache[roadmap.id]) {
        throw new Error(
          'Do not store partial roadmaps that are not already in the cache'
        );
      }
      if (isPartialRoadmap) {
        // Extract only the properties that can be updated via partial roadmap updates.
        // Add new editable properties to this object as needed.
        const updates: Pick<
          Roadmap,
          'name' | 'description' | 'roadmapGroupId'
        > = {
          name: roadmap.name,
          description: roadmap.description,
          roadmapGroupId: roadmap.roadmapGroupId,
        };

        // Update cache
        Object.assign(this.ganttRoadmapsCache[roadmap.id], updates);

        // Update list entry
        const listItem = this.ganttRoadmapsList.find(
          (c) => c.id === roadmap.id
        );
        if (listItem) {
          Object.assign(listItem, updates);
        }
      } else {
        this.ganttRoadmapsCache[roadmap.id] = roadmap;

        const existingIdx = this.ganttRoadmapsList.findIndex(
          (c) => c.id === roadmap.id
        );
        if (existingIdx === -1) this.ganttRoadmapsList.push(roadmap);
        else this.ganttRoadmapsList[existingIdx] = roadmap;

        roadmap.initiatives.forEach((initiative) => {
          this.ganttItemsChildrenLoadedMap[initiative.id] = true;
        });
      }

      // setup pipes for async updates as soon as we have a roadmap
      if (!this.isSubscribedToAsyncUpdates) {
        // TODO: ideally this should be called as soon as the store is created. figure out a way later
        this._subscribeToItemUpdates();
      }
    },

    _removeRoadmap(roadmapId: Roadmap['id']) {
      delete this.ganttRoadmapsCache[roadmapId];
      this.ganttRoadmapsList = this.ganttRoadmapsList.filter(
        (c) => c.id !== roadmapId
      );
    },

    _addInitiative(roadmapId: Roadmap['id'], initiative: Initiative) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      const exists = roadmap.initiatives.some((i) => i.id === initiative.id);
      if (exists) return;
      roadmap.initiatives.push(initiative);
    },

    _updateInitiative(roadmapId: Roadmap['id'], initiative: Initiative) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      const initiativeIdx = roadmap.initiatives.findIndex(
        (i) => i.id === initiative.id
      );
      if (initiativeIdx === -1) return;
      roadmap.initiatives[initiativeIdx] = initiative;
      // Invalidate cache since property changes aren't detected by ID-based hash
      this._itemsVersion++;
    },

    _deleteInitiative(
      roadmapId: Roadmap['id'],
      initiativeId: Initiative['id']
    ) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (roadmap) {
        // remove initiative from roadmap
        roadmap.initiatives = roadmap.initiatives.filter(
          (i) => i.id !== initiativeId
        );
      }
    },

    _addMilestone(roadmapId: Roadmap['id'], milestone: Milestone) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      const exists = roadmap.milestones.some((m) => m.id === milestone.id);
      if (exists) return;
      roadmap.milestones.push(milestone);
    },

    _updateMilestone(roadmapId: Roadmap['id'], milestone: Milestone) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      const milestoneIdx = roadmap.milestones.findIndex(
        (m) => m.id === milestone.id
      );
      if (milestoneIdx === -1) return;
      roadmap.milestones[milestoneIdx] = milestone;
      // Invalidate cache since property changes aren't detected by ID-based hash
      this._itemsVersion++;
    },

    _deleteMilestone(roadmapId: Roadmap['id'], milestoneId: Milestone['id']) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      roadmap.milestones = roadmap.milestones.filter(
        (m) => m.id !== milestoneId
      );
    },

    /** for updating Roadmap['roadmapItems']. No relation to work items/folders. */
    _updateRoadmapItem(roadmapId: Roadmap['id'], item: RoadmapItem) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      const itemIdx = roadmap.roadmapItems.findIndex(
        (i) => i.itemNumber === item.itemNumber
      );
      if (itemIdx === -1) return;
      roadmap.roadmapItems.splice(itemIdx, 1, item);
      // Invalidate cache since property changes aren't detected by ID-based hash
      this._itemsVersion++;
    },

    _addScenario(roadmapId: Roadmap['id'], scenario: Scenario) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      if (!roadmap.scenarios) roadmap.scenarios = [];
      roadmap.scenarios.push(scenario);
    },

    _updateScenario(roadmapId: Roadmap['id'], scenario: Scenario) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      const scenarioIdx = roadmap.scenarios.findIndex(
        (s) => s.id === scenario.id
      );
      if (scenarioIdx === -1) return;
      roadmap.scenarios.splice(scenarioIdx, 1, scenario);
    },

    _deleteScenario(roadmapId: Roadmap['id'], scenarioId: Scenario['id']) {
      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) return;
      roadmap.scenarios = roadmap.scenarios.filter((s) => s.id !== scenarioId);
    },

    _queue(
      task: () => Promise<unknown>,
      { next = false } = {}
    ): Promise<unknown> {
      const currentUpdateQueue = this.currentGanttRoadmapId
        ? this.updateQueues[this.currentGanttRoadmapId]
        : null;
      if (currentUpdateQueue) {
        if (next) {
          return currentUpdateQueue.enqueueNext(task);
        } else {
          return currentUpdateQueue.enqueue(task);
        }
      } else {
        return task();
      }
    },

    _queueItemUpdates() {
      if (this.isItemUpdateQueued) {
        return;
      }

      const flush = () => {
        // flush work item/folder updates
        const itemStore = useItemStore();
        const items = itemStore.itemsCaches[ItemsCacheKeys.GANTT_ITEMS] || [];

        // Efficient incremental update: only clone items that have changed
        // Uses object reference comparison to detect changes without relying on dateUpdated
        const existingMap = new Map<PreparedItem['number'], PreparedItem>();
        this.localAllItems.forEach((i) => existingMap.set(i.number, i));

        // Track which items are still present in the new set
        const currentNumbers = new Set<PreparedItem['number']>();

        // Process updates and additions
        items.forEach((item) => {
          const rawItem = toRaw(item);
          currentNumbers.add(rawItem.number);

          const existingItem = existingMap.get(rawItem.number);

          // New item - clone and add
          if (!existingItem) {
            existingMap.set(rawItem.number, cloneDeep(rawItem));
            return;
          }

          // Check if the item reference changed (Vue replaces reactive objects when properties change)
          // Also check key fields that might change during drag operations or inline edits
          const existingRaw = toRaw(existingItem);
          if (
            existingRaw.startDate !== rawItem.startDate ||
            existingRaw.dueDate !== rawItem.dueDate ||
            existingRaw.parentRank !== rawItem.parentRank ||
            existingRaw.relationships?.length !==
              rawItem.relationships?.length ||
            existingRaw.manualPercentCompleted !==
              rawItem.manualPercentCompleted ||
            ('completedAt' in existingRaw &&
              'completedAt' in rawItem &&
              existingRaw.completedAt !== rawItem.completedAt) ||
            ('statusId' in existingRaw &&
              'statusId' in rawItem &&
              existingRaw.statusId !== rawItem.statusId) ||
            ('boardId' in existingRaw &&
              'boardId' in rawItem &&
              existingRaw.boardId !== rawItem.boardId)
          ) {
            existingMap.set(rawItem.number, cloneDeep(rawItem));
          }
          // Otherwise keep existing item (no clone needed)
        });

        // Remove deleted items
        existingMap.forEach((_, number) => {
          if (!currentNumbers.has(number)) {
            existingMap.delete(number);
          }
        });

        this.localAllItems = [...existingMap.values()];
        this._itemsVersion++;

        // flush milestone updates
        this.pendingMilestoneUpdates.forEach((milestone) => {
          this._updateMilestone(milestone.roadmapId, milestone);
        });
        this.pendingMilestoneUpdates = [];

        this.isItemUpdateQueued = false;
      };

      this.isItemUpdateQueued = true;
      this._queue(
        async () => {
          flush();
        },
        { next: true }
      );
    },

    _subscribeToItemUpdates() {
      if (this.isSubscribedToAsyncUpdates) return;

      this.isSubscribedToAsyncUpdates = true;

      const initiativeCreatedHandler = (
        event: PusherInitiativeCreatedEvent
      ) => {
        const { initiative } = event.data;
        this._addInitiative(initiative.roadmapId, initiative);
      };
      const initiativeUpdatedHandler = (
        event: PusherInitiativeUpdatedEvent
      ) => {
        const { initiativeAfter: initiative } = event.data;
        this._updateInitiative(initiative.roadmapId, initiative);
      };
      const initiativeDeletedHandler = (
        event: PusherInitiativeDeletedEvent
      ) => {
        const { initiative } = event.data;
        this._deleteInitiative(initiative.roadmapId, initiative.id);
      };

      const milestoneCreatedHandler = (event: PusherMilestoneCreatedEvent) => {
        const { milestone } = event.data;
        this._addMilestone(milestone.roadmapId, milestone);
      };
      const milestoneUpdatedHandler = (event: PusherMilestoneUpdatedEvent) => {
        const { milestoneAfter: milestone } = event.data;
        this.pendingMilestoneUpdates.push(milestone);
        this._queueItemUpdates();
      };
      const milestoneDeletedHandler = (event: PusherMilestoneDeletedEvent) => {
        const { milestone } = event.data;
        this._deleteMilestone(milestone.roadmapId, milestone.id);
      };

      const scenarioCreatedHandler = (event: PusherScenarioCreatedEvent) => {
        const { scenario } = event.data;
        this._addScenario(scenario.roadmapId, scenario);
      };
      const scenarioUpdatedHandler = (event: PusherScenarioUpdatedEvent) => {
        const { scenarioAfter: scenario } = event.data;
        this._updateScenario(scenario.roadmapId, scenario);
      };
      const scenarioDeletedHandler = (event: PusherScenarioDeletedEvent) => {
        const { scenario } = event.data;
        this._deleteScenario(scenario.roadmapId, scenario.id);
      };

      ChannelEvents.on('initiative-created', initiativeCreatedHandler);
      ChannelEvents.on('initiative-updated', initiativeUpdatedHandler);
      ChannelEvents.on('initiative-deleted', initiativeDeletedHandler);

      ChannelEvents.on('milestone-created', milestoneCreatedHandler);
      ChannelEvents.on('milestone-updated', milestoneUpdatedHandler);
      ChannelEvents.on('milestone-deleted', milestoneDeletedHandler);

      ChannelEvents.on('scenario-created', scenarioCreatedHandler);
      ChannelEvents.on('scenario-updated', scenarioUpdatedHandler);
      ChannelEvents.on('scenario-deleted', scenarioDeletedHandler);

      const itemStore = useItemStore();
      const unsubscribeWatch = watch(
        () => itemStore.itemsCaches[ItemsCacheKeys.GANTT_ITEMS],
        () => {
          this._queueItemUpdates();
        },
        { deep: true, immediate: true }
      );

      // Store cleanup function to be called when component unmounts
      this._cleanupItemUpdates = () => {
        ChannelEvents.off('initiative-created', initiativeCreatedHandler);
        ChannelEvents.off('initiative-updated', initiativeUpdatedHandler);
        ChannelEvents.off('initiative-deleted', initiativeDeletedHandler);

        ChannelEvents.off('milestone-created', milestoneCreatedHandler);
        ChannelEvents.off('milestone-updated', milestoneUpdatedHandler);
        ChannelEvents.off('milestone-deleted', milestoneDeletedHandler);

        ChannelEvents.off('scenario-created', scenarioCreatedHandler);
        ChannelEvents.off('scenario-updated', scenarioUpdatedHandler);
        ChannelEvents.off('scenario-deleted', scenarioDeletedHandler);

        unsubscribeWatch();

        this.isSubscribedToAsyncUpdates = false;
        this._cleanupItemUpdates = null;
      };
    },

    // ------------------------------ public actions ------------------------------ //

    /** clears the loaded items and loaded roadmaps cache to create a clean-ish slate. Useful for applying filters. */
    clear() {
      const itemStore = useItemStore();
      itemStore.clearItems(ItemsCacheKeys.GANTT_ITEMS);
      this.localAllItems = [];
      this._itemsVersion++;
      this._preparedRoadmapCache = {};
      this.ganttItemsChildrenLoadedMap = {};
      this.ganttRoadmapsCache = {};
      // Clear additional state that can accumulate across navigation cycles
      this.pendingMilestoneUpdates = [];
      this.isItemUpdateQueued = false;
      // Clear optimistic updates for all roadmaps to prevent memory accumulation
      this.optimisticUpdatesMap = {};
      // Clear each promise queue's pending tasks before discarding them to prevent retained closures
      Object.values(this.updateQueues).forEach((queue) => queue.clear());
      this.updateQueues = {};
      this.currentGanttRoadmapId = null;
      this.scenarioModel = null;
      // ganttRoadmapsList/Groups preserved - used by dropdown selector, only fetched on page load
      // Cleanup pending async handlers to prevent memory leaks
      this._pendingAsyncHandlers.forEach((handler) => handler.cleanup());
      this._pendingAsyncHandlers = [];
    },

    /** Cleanup function to be called from component's onUnmounted hook */
    unsubscribeFromItemUpdates() {
      if (this._cleanupItemUpdates) {
        this._cleanupItemUpdates();
      }
      // Cleanup pending async handlers to prevent memory leaks
      this._pendingAsyncHandlers.forEach((handler) => handler.cleanup());
      this._pendingAsyncHandlers = [];
    },

    setWorkOffline(workOffline: boolean) {
      this.workOffline = workOffline;
    },

    setSelectedGanttRoadmapId(roadmapId: Roadmap['id'] | null) {
      this.currentGanttRoadmapId = roadmapId;
      if (roadmapId && !this.updateQueues[roadmapId]) {
        this.updateQueues[roadmapId] = usePromiseQueue();
      }
    },

    async createGanttRoadmap(data: CreateRoadmap) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.createRoadmap<true>({
          path: { organizationId },
          body: data,
        });
        const roadmap = response.data.data;
        this._storeRoadmap(roadmap);
        await this._storeRoadmapItems([
          ...roadmap.folders,
          ...roadmap.workItems,
        ]);
        return roadmap;
      });
    },

    async createRoadmapGroup(data: CreateRoadmapGroup) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.createRoadmapGroup<true>({
          path: { organizationId },
          body: data,
        });
        const group = response.data.data;
        if (group) {
          this.ganttRoadmapsGroups.push(group);
        }
        return group;
      });
    },

    async updateRoadmapGroup(
      roadmapGroupId: RoadmapGroup['id'],
      data: UpdateRoadmapGroup
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateRoadmapGroup<true>({
          path: { organizationId, roadmapGroupId },
          body: data,
        });
        const group = response.data.data;
        if (group) {
          const idx = this.ganttRoadmapsGroups.findIndex(
            (g) => g.id === group.id
          );
          this.ganttRoadmapsGroups[idx] = group;
        }
        return group;
      });
    },

    async deleteRoadmapGroup(roadmapGroupId: RoadmapGroup['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.deleteRoadmapGroup<true>({
          path: { organizationId, roadmapGroupId },
        });
        this.fetchGanttRoadmaps();
        if (response.data.success) {
          this.ganttRoadmapsGroups = this.ganttRoadmapsGroups.filter(
            (g) => g.id !== roadmapGroupId
          );
        }
        return response.data;
      });
    },
    async findRoadmapGroups() {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.findRoadmapGroups<true>({
          path: { organizationId },
        });
        if (response.data.data) {
          this.ganttRoadmapsGroups = response.data.data;
        }
        return response.data.data;
      });
    },

    async fetchGanttRoadmaps() {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.findRoadmaps<true>({
          path: { organizationId },
        });
        this.ganttRoadmapsList = response.data.data;
        return this.ganttRoadmapsList;
      });
    },
    async fetchGanttRoadmap(id: Roadmap['id'], query?: FindItemsData['query']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      const itemStore = useItemStore();

      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.getRoadmapById<true>({
          path: { organizationId, roadmapId: id },
        });
        const roadmap = response.data.data;

        if (query) {
          const pFolders = [];
          for (const number of roadmap.folders.map(
            (f: { number: string }) => f.number
          )) {
            pFolders.push(
              (async () => {
                if (!roadmap.folders.length) return null;
                return await itemStore.search({
                  query: {
                    ...query,
                    limit: roadmap.folders.length,
                    number: [number],
                    itemType: ItemType.FOLDER,
                  },
                });
              })()
            );
          }
          const pWorkItems = [];
          for (const number of roadmap.workItems.map(
            (w: { number: string }) => w.number
          )) {
            pWorkItems.push(
              (async () => {
                if (!roadmap.workItems.length) return null;
                return await itemStore.search({
                  query: {
                    ...query,
                    limit: roadmap.workItems.length,
                    number: [number],
                    itemType: ItemType.WORKITEM,
                    checkChildren: true,
                  },
                });
              })()
            );
          }
          const items = await Promise.all([...pFolders, ...pWorkItems]);
          const flatItems = items.flatMap((item) => item?.items || []);

          const folderResponse = flatItems.filter(
            (i) => i.itemType === ItemType.FOLDER
          );
          const workItemResponse = flatItems.filter(
            (i) => i.itemType === ItemType.WORKITEM
          );
          const filteredRoadmap: Roadmap = {
            ...roadmap,
            folders: folderResponse as PreparedFolder[],
            workItems: workItemResponse as PreparedWorkItem[],
          };
          // Store the filtered roadmap and its items
          this._storeRoadmap(filteredRoadmap);
          await this._storeRoadmapItems([
            ...filteredRoadmap.folders,
            ...filteredRoadmap.workItems,
          ]);
          return filteredRoadmap;
        } else {
          // If there's no query, store the original roadmap and its items
          this._storeRoadmap(roadmap);
          await this._storeRoadmapItems([
            ...roadmap.folders,
            ...roadmap.workItems,
          ]);
          return roadmap;
        }
      });
    },

    async updateGanttRoadmap(
      ganttRoadmapId: Roadmap['id'],
      data: UpdateRoadmap
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateRoadmap<true>({
          path: { organizationId, roadmapId: ganttRoadmapId },
          body: data,
        });
        const roadmap = response.data.data;
        this._storeRoadmap(roadmap, true);
        return response.data.data;
      });
    },

    async deleteGanttRoadmap(ganttRoadmapId: Roadmap['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.deleteRoadmap<true>({
          path: { organizationId, roadmapId: ganttRoadmapId },
        });
        this.setSelectedGanttRoadmapId(null);
        this._removeRoadmap(ganttRoadmapId);
        return response.data;
      });
    },

    async createGanttInitiative(
      ganttRoadmapId: Roadmap['id'],
      data: CreateInitiative
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.createInitiative<true>({
          path: { organizationId },
          body: data,
        });
        const initiative = response.data.data;
        this._addInitiative(initiative.roadmapId, initiative);
        return initiative;
      });
    },

    async updateGanttInitiative(
      ganttRoadmapId: Roadmap['id'],
      ganttInitiativeId: Initiative['id'],
      data: UpdateInitiative
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateInitiative<true>({
          path: { organizationId, initiativeId: ganttInitiativeId },
          body: data,
        });
        const initiative = response.data.data;
        return initiative;
      });
    },

    async deleteGanttInitiative(
      ganttRoadmapId: Roadmap['id'],
      ganttInitiativeId: Initiative['id']
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.deleteInitiative<true>({
          path: { organizationId, initiativeId: ganttInitiativeId },
        });

        return response.data;
      });
    },

    async createGanttMilestone(
      ganttRoadmapId: Roadmap['id'],
      data: CreateMilestone
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.createMilestone<true>({
          path: { organizationId },
          body: data,
        });
        const milestone = response.data.data;
        this._addMilestone(milestone.roadmapId, milestone);
        return milestone;
      });
    },

    async updateGanttMilestone(
      ganttRoadmapId: Roadmap['id'],
      ganttMilestoneId: Milestone['id'],
      data: UpdateMilestone
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateMilestone<true>({
          path: { organizationId, milestoneId: ganttMilestoneId },
          body: data,
        });
        const milestone = response.data.data;
        this.pendingMilestoneUpdates.push(milestone);
        this._queueItemUpdates();
        return response.data;
      });
    },

    async deleteGanttMilestone(
      ganttRoadmapId: Roadmap['id'],
      ganttMilestoneId: Milestone['id']
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.deleteMilestone<true>({
          path: { organizationId, milestoneId: ganttMilestoneId },
        });

        this._deleteMilestone(ganttRoadmapId, ganttMilestoneId);
        return response.data;
      });
    },

    async updateGanttRoadmapItem(
      ganttRoadmapId: Roadmap['id'],
      ganttItemNumber: PreparedItem['number'],
      data: UpdateRoadmapItem
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateRoadmapItem<true>({
          path: {
            organizationId,
            roadmapId: ganttRoadmapId,
            itemNumber: ganttItemNumber,
          },
          body: data,
        });
        const item = response.data.data;
        this._updateRoadmapItem(ganttRoadmapId, item);
        return item;
      });
    },

    async updateItemsInGanttInitiative(
      ganttRoadmapId: Roadmap['id'],
      ganttInitiativeId: Initiative['id'],
      /**
       * Just the item numbers to add or remove. Existing items will not be affected.
       */
      items: PreparedItem[],
      remove?: boolean
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      // Look in cache first, then fall back to list (for "Add to Gantt" from items table)
      const roadmap =
        this.ganttRoadmapsCache[ganttRoadmapId] ||
        this.ganttRoadmapsList.find((r) => r.id === ganttRoadmapId);
      if (!roadmap) {
        throw new Error('Roadmap not found');
      }
      const initiative = roadmap.initiatives.find(
        (initiative) => initiative.id === ganttInitiativeId
      );
      if (!initiative) {
        throw new Error('Initiative not found');
      }
      const passedItemNumbers = items.map((item) => item.number);
      let itemNumbers = [...initiative.itemNumbers];
      if (remove) {
        itemNumbers = itemNumbers.filter(
          (itemNumber) => !passedItemNumbers.includes(itemNumber)
        );
      } else {
        itemNumbers = [...itemNumbers, ...passedItemNumbers];
      }

      const payload: UpdateInitiative = {
        itemNumbers,
      };
      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateInitiative<true>({
          path: { organizationId, initiativeId: ganttInitiativeId },
          body: payload,
        });
        // adding items to the initiative could potentially change other initiatives, so do a refetch
        await this.fetchGanttRoadmap(ganttRoadmapId);
        const initiative = response.data.data;
        return initiative;
      });
    },

    async fetchInitiativeChildren(
      ganttRoadmapId: Roadmap['id'],
      initiativeId: Initiative['id']
    ) {
      const roadmap = this.ganttRoadmapsCache[ganttRoadmapId];
      if (!roadmap) {
        throw new Error('Roadmap not found');
      }
      const initiative = roadmap.initiatives.find(
        (initiative) => initiative.id === initiativeId
      );
      if (!initiative) {
        throw new Error('Initiative not found');
      }

      const itemStore = useItemStore();
      const pItems = initiative.itemNumbers.map(async (itemNumber) => {
        const itemType = itemNumber.startsWith('F')
          ? ItemType.FOLDER
          : ItemType.WORKITEM;
        return await itemStore.load({
          skipAddingCache: true,
          itemNumber,
          itemType,
        });
      });
      const items = await Promise.all(pItems);
      this.ganttItemsChildrenLoadedMap[initiativeId] = true;
      await this._storeRoadmapItems(items);
    },

    async fetchChildren(
      ganttRoadmapId: Roadmap['id'],
      {
        parentNumber,
        filters,
        skipAddingItems = false,
      }: {
        parentNumber: PreparedItem['number'];
        filters?: StoredQueryOptions;
        /** skip adding the fetched items to the cache. Useful if the caller wants to implement a throttling mechanism */
        skipAddingItems?: boolean;
      }
    ) {
      const { fetchChildItems, items, folders } = useFetchChildren({
        query: {
          limit: 10_000,
          parentNumber: [parentNumber],
          ...filters,
        },
        preventFilteringPlannedFolders: true,
        checkItemChildren: filters ? areFiltersApplied(filters) : undefined,
      });
      await fetchChildItems();
      const allItems: PreparedItem[] = [...folders.value, ...items.value];
      this.ganttItemsChildrenLoadedMap[parentNumber] = true;

      if (!skipAddingItems) {
        await this._storeRoadmapItems(allItems, { updateAsync: true });
      }

      return allItems;
    },

    setInteractionMode(mode: GanttState['interactionMode']) {
      this.interactionMode = mode;
    },

    setScenarioModel(model: GanttState['scenarioModel']) {
      this.scenarioModel = model;

      if (!this.currentGanttRoadmapId) return; // no need for any additional processing

      // clear all date updates
      this.optimisticUpdatesMap[this.currentGanttRoadmapId] =
        this.optimisticUpdatesMap[this.currentGanttRoadmapId]?.filter(
          (u) => !u.update.startDate && !u.update.dueDate
        ) || [];
      this._optimisticVersions[this.currentGanttRoadmapId] =
        (this._optimisticVersions[this.currentGanttRoadmapId] || 0) + 1;

      // apply snapshot if needed
      if (model?.type === 'record-edit') {
        const snapshot = model.data.snapshot;
        this.optimisticUpdatesMap[this.currentGanttRoadmapId] = [
          ...(this.optimisticUpdatesMap[this.currentGanttRoadmapId] || []),
        ];
        const updates = this.optimisticUpdatesMap[this.currentGanttRoadmapId];
        Object.entries(snapshot).forEach(([id, dates]) => {
          const updateKey = 'snapshot-update-' + uuidv4(); // the prefix is so we can identify these updates for displaying; not a great solution
          const optUpdate: OptimisticUpdate = {
            id,
            updateKey,
            update: {
              startDate: dates.startDate || undefined, // undefined for milestones
              dueDate: dates.dueDate,
            },
            mode: 'flexible',
          };
          updates.push(optUpdate);
        });
      }
    },

    createGanttScenario(ganttRoadmapId: Roadmap['id'], data: CreateScenario) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.createScenario<true>({
          path: { organizationId },
          body: data,
        });
        const scenario = response.data.data;
        return scenario;
      });
    },

    updateGanttScenario(
      ganttRoadmapId: Roadmap['id'],
      ganttScenarioId: Scenario['id'],
      data: UpdateScenario
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.updateScenario<true>({
          path: { organizationId, scenarioId: ganttScenarioId },
          body: data,
        });
        const scenario = response.data.data;
        this._updateScenario(ganttRoadmapId, scenario);
        return scenario;
      });
    },

    deleteGanttScenario(
      ganttRoadmapId: Roadmap['id'],
      ganttScenarioId: Scenario['id']
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await RoadmapService.deleteScenario<true>({
          path: { organizationId, scenarioId: ganttScenarioId },
        });

        this._deleteScenario(ganttRoadmapId, ganttScenarioId);
        return response.data;
      });
    },

    removeOptimisticUpdate(
      roadmapId: Roadmap['id'],
      updateKey: OptimisticUpdate['updateKey']
    ) {
      const updates = this.optimisticUpdatesMap[roadmapId];
      if (!updates) return;
      const updateIdx = updates.findIndex((u) => u.updateKey === updateKey);
      if (updateIdx === -1) return;
      updates.splice(updateIdx, 1);
      this._optimisticVersions[roadmapId] =
        (this._optimisticVersions[roadmapId] || 0) + 1;
    },

    clearOptimisticUpdates(roadmapId: Roadmap['id']) {
      this.optimisticUpdatesMap[roadmapId] = [];
      this._optimisticVersions[roadmapId] =
        (this._optimisticVersions[roadmapId] || 0) + 1;
    },

    _handleAsyncUpdateCompletion(correlationId: string) {
      let resolve: (value?: unknown) => void;
      let reject: (reason?: unknown) => void;
      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });

      let created = false;
      let handled = false;
      const timeoutIds: ReturnType<typeof setTimeout>[] = [];

      // Capture store reference for use in hoisted function
      const pendingAsyncHandlers = this._pendingAsyncHandlers;

      // Use function declaration for cleanup (hoisted) to allow mutual references
      // between jobHandler and cleanup without temporal dead zone issues
      function cleanup() {
        ChannelEvents.off('job-updated', jobHandler);
        timeoutIds.forEach((id) => clearTimeout(id));
        // Remove this handler from tracking array
        const idx = pendingAsyncHandlers.findIndex(
          (h) => h.jobHandler === jobHandler
        );
        if (idx !== -1) {
          pendingAsyncHandlers.splice(idx, 1);
        }
      }

      const jobHandler = (event: PusherJobUpdatedEvent) => {
        const job = event.data.jobAfter;
        if (job.correlationId !== correlationId) return; // not my job :^)

        // mark created
        if (
          job.status === JobStatus.PENDING ||
          job.status === JobStatus.RUNNING
        ) {
          created = true;
          return;
        }

        // ignore irrelevant updates
        if (
          job.status !== JobStatus.COMPLETED &&
          job.status !== JobStatus.FAILED
        ) {
          return; // some other status, ignore
        }

        // mark handled and handle completion
        handled = true;
        cleanup();
        if (get(job.output, 'success')) {
          resolve();
        } else {
          const errorMessage = `Job failed for updating cascading gantt dates: ${job.id}`;
          console.trace(errorMessage, job);
          reject(new Error(errorMessage));
        }
      };

      // wait for the job to finish
      ChannelEvents.on('job-updated', jobHandler);

      // if the job is not created within the timeout, assume it's not needed
      timeoutIds.push(
        setTimeout(() => {
          if (created) return;
          handled = true;
          cleanup();
          resolve(); // assume no async update was needed
        }, ASYNC_JOB_CREATION_TIMEOUT_MS)
      );

      // if the job is not handled within the timeout, assume it worked
      timeoutIds.push(
        setTimeout(() => {
          if (handled) return;
          cleanup();
          resolve(); // assume it worked
        }, ASYNC_JOB_COMPLETION_TIMEOUT_MS)
      );

      // Track this handler for cleanup on unmount
      this._pendingAsyncHandlers.push({
        jobHandler,
        timeoutIds,
        cleanup,
      });

      return promise;
    },

    async updateGanttItem(
      roadmapId: Roadmap['id'],
      item: PreparedItem | PreparedGanttMilestone | PreparedGanttInitiative,
      data: UpdateGanttItem
    ) {
      // ----------------- preliminary checks ----------------- //
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      const roadmap = this.ganttRoadmapsCache[roadmapId];
      if (!roadmap) {
        throw new Error('Roadmap not found');
      }

      // ----------------- prepare optimistic update ----------------- //
      const updateKey = uuidv4();
      this.optimisticUpdatesMap[roadmapId] =
        this.optimisticUpdatesMap[roadmapId] || [];
      this.optimisticUpdatesMap[roadmapId].push({
        updateKey,
        id: getGanttItemIdentifier(item),
        update: data,
        mode: this.interactionMode,
      });
      this._optimisticVersions[roadmapId] =
        (this._optimisticVersions[roadmapId] || 0) + 1;

      if (this.workOffline) return;
      if (this.isRecordingScenario) {
        // ignore date updates when recording scenario
        if (data.dueDate || data.startDate) return;
      }

      // ----------------- prepare variables for actual update ----------------- //
      const isMilestone = item.itemType === GanttItemType.MILESTONE;
      const isInitiative = item.itemType === GanttItemType.INITIATIVE;
      const isFirstLevelItem = (() => {
        if (isMilestone || isInitiative) return false;
        const allFirstLevelNumbers = roadmap.initiatives.flatMap(
          (initiative) => initiative.itemNumbers
        );
        return allFirstLevelNumbers.includes(item.number);
      })();

      if (data.rankBelow && (isMilestone || isInitiative) && !data.rankEntity) {
        throw new Error(
          'Rank entity is required for updating milestone/initiative rank'
        );
      }

      const itemStore = useItemStore();

      // Lazy-build itemsMap only when needed (for relationship lookups)
      // This avoids O(n) map building for common operations like date/rank changes
      type ItemsMapValue = {
        item: PreparedItem | Milestone;
        type: 'workitem' | 'folder' | 'milestone';
      };
      let _itemsMap: Map<
        Milestone['id'] | PreparedItem['number'],
        ItemsMapValue
      > | null = null;
      const getItemsMap = () => {
        if (_itemsMap) return _itemsMap;
        _itemsMap = new Map();
        roadmap.milestones.forEach((milestone) => {
          _itemsMap!.set(milestone.id, {
            item: milestone,
            type: 'milestone',
          });
        });
        itemStore.itemsCaches[ItemsCacheKeys.GANTT_ITEMS].forEach((item) => {
          _itemsMap!.set(item.number, {
            item,
            type: item.itemType,
          });
        });
        return _itemsMap;
      };

      const requiresAsyncUpdateHandling = (() => {
        if (isInitiative) return false;
        if (this.interactionMode !== 'strict') return false;

        // adding deps will require date movements if there are missing dates or dependent's start date < dependency's end date
        // TODO: make this more accurate
        if (data.relationshipsAdded) return true;

        // due date movements will require async handling if there are dependents (because their dates will move too)
        if (
          data.dueDate &&
          item.relationships?.some((rel) => rel.type === 'blocks')
        )
          return true;

        return false;
      })();
      const asyncUpdateHandler = (
        response: ApiResponseBase<unknown> | undefined
      ) => {
        const correlationId = response?.devstrideCorrelationId;
        if (!requiresAsyncUpdateHandling || !correlationId) return response;
        return this._handleAsyncUpdateCompletion(correlationId);
      };

      const promiseCreators: (() => Promise<unknown>)[] = [];

      // ----------------- handle remote updates for rank ----------------- //
      if (data.useWhichRank) {
        if (!data[data.useWhichRank]) throw Error('Pass the rank');
        let p: () => Promise<unknown>;
        if (isMilestone) {
          const payload: UpdateMilestone = {
            [data.useWhichRank]: data[data.useWhichRank],
            rankEntity: data.rankEntity,
          };
          p = () => this.updateGanttMilestone(roadmapId, item.id, payload);
        } else if (isInitiative) {
          const payload: UpdateInitiative = {
            [data.useWhichRank]: data[data.useWhichRank],
            rankEntity: data.rankEntity,
          };
          p = () => this.updateGanttInitiative(roadmapId, item.id, payload);
        } else if (isFirstLevelItem) {
          const initiative = roadmap.initiatives.find((initiative) =>
            initiative.itemNumbers.includes(item.number)
          );
          if (!initiative) throw new Error('Initiative not found for item');
          const payload: UpdateRoadmapItem = {
            initiativeId: initiative.id,
            [data.useWhichRank]: data[data.useWhichRank],
          };
          p = () =>
            this.updateGanttRoadmapItem(roadmapId, item.number, payload);
        } else {
          const payload: UpdateItem = {
            [data.useWhichRank === 'rankAbove'
              ? 'parentRankAbove'
              : 'parentRankBelow']: data[data.useWhichRank],
          };
          const itemStore = useItemStore();
          p = () =>
            itemStore.updateWorkItem({
              workItemNumber: item.number,
              itemType: item.itemType,
              data: payload,
            });
        }
        if (p) promiseCreators.push(p);
      }

      // ----------------- handle remote updates for relationships ----------------- //
      if (data.relationshipsAdded || data.relationshipsRemoved) {
        if (isInitiative) {
          throw new Error(
            'Relationships cannot be added/removed from initiatives'
          );
        }

        // prepare payload
        const staticMode = this.interactionMode === 'strict';

        let relationships: Relationship[] = [...item.relationships];
        if (data.relationshipsAdded) {
          const itemsMap = getItemsMap();
          relationships.push(
            ...data.relationshipsAdded.map((addedRel): Relationship => {
              const dep = itemsMap.get(addedRel.entityId);
              if (!dep) throw new Error('Relation item not found');
              return {
                entity: dep.type as RelationshipEntity,
                referenceId: addedRel.entityId,
                type: addedRel.relationType,
              };
            })
          );
        }
        if (data.relationshipsRemoved) {
          const removedIds = data.relationshipsRemoved;
          relationships = relationships.filter(
            (rel) => !removedIds.includes(rel.referenceId)
          );
        }

        // prepare update function

        let p: () => Promise<unknown>;

        if (isMilestone) {
          p = () =>
            this.updateGanttMilestone(roadmapId, item.id, {
              relationships,
              staticMode,
            }).then(asyncUpdateHandler);
        } else {
          p = () =>
            itemStore
              .updateItemRelationships({
                workItemNumber: item.number,
                itemType: item.itemType,
                data: {
                  relationships,
                  staticMode,
                },
              })
              .then(asyncUpdateHandler);
        }
        if (p) promiseCreators.push(p);
      }

      // ----------------- handle remote updates for dates ----------------- //
      if (data.startDate || data.dueDate) {
        if (isInitiative) {
          throw new Error(
            'Start date and/or due date cannot be updated for initiatives'
          );
        }
        if (isMilestone && !data.dueDate) {
          throw new Error('Due date is required for milestones');
        }

        if (this.currentGanttRoadmap) {
          // Get the items that will be affected by the update
          const touchedItems = calculateOptimisticTime(
            this.currentGanttRoadmap,
            {
              updateKey: 'temp-calculation',
              id: getGanttItemIdentifier(item),
              update: data,
              mode: this.interactionMode,
            },
            false
          );
          const affectedItemsCount = Object.keys(touchedItems).length;
          if (affectedItemsCount > 1) {
            const dynamicTimeout =
              affectedItemsCount * DYNAMIC_TIMEOUT_PER_ITEM_MS;
            const totalTimeout = Math.min(
              DYNAMIC_TIMEOUT_BASE_MS + dynamicTimeout,
              MAX_DYNAMIC_TIMEOUT_MS
            );
            promiseCreators.push(() => timeout(totalTimeout));
          }
        }

        const staticMode = this.interactionMode === 'strict';

        let p: () => Promise<unknown>;
        const toFormattedDate = (str: string) =>
          dayjs(str).format('YYYY-MM-DD');
        if (isMilestone) {
          const payload: UpdateMilestone = {
            staticMode,
          };
          if (data.dueDate) {
            payload.dueDate = toFormattedDate(data.dueDate);
          }
          p = () =>
            this.updateGanttMilestone(roadmapId, item.id, payload).then(
              asyncUpdateHandler
            );
        } else {
          const itemStore = useItemStore();
          const payload: UpdateItem = {
            staticMode,
          };
          if (data.startDate) {
            payload.startDate = toFormattedDate(data.startDate);
          }
          if (data.dueDate) {
            payload.dueDate = toFormattedDate(data.dueDate);
          }
          if (data.boardId) {
            (payload as UpdateWorkItem).boardId = data.boardId;
          }

          p = () =>
            itemStore
              .updateWorkItem({
                workItemNumber: item.number,
                itemType: item.itemType,
                data: payload,
              })
              .then(asyncUpdateHandler);
        }

        if (p) promiseCreators.push(p);
      }

      // ----------------- queue the update and cleanup ----------------- //
      const clearOptimisticUpdate = () => {
        const idx = this.optimisticUpdatesMap[roadmapId].findIndex(
          (update) => update.updateKey === updateKey
        );
        if (idx !== -1) {
          this.optimisticUpdatesMap[roadmapId].splice(idx, 1);
        }
      };
      const update = () => {
        return apiRequestHandler(
          async () => {
            // TODO: handle error from async update handler and throw others to apiRequestHandler
            await Promise.all(promiseCreators.map((p) => p()));
            clearOptimisticUpdate();
          },
          {
            errorCallback: () => {
              clearOptimisticUpdate();
            },
          }
        );
      };
      return this._queue(update);
    },
  },
});
